import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import Speedometer from './Speedometer';

const ComplianceDashboard = ({ db, usersList, currentDate }) => {
    const [dashboardTab, setDashboardTab] = useState('drugAlcohol');
    const [drugAlcoholTab, setDrugAlcoholTab] = useState('alcohol');
    const [complianceRecords, setComplianceRecords] = useState([]);
    const [gateHistory, setGateHistory] = useState([]);
    const [gateFormOpen, setGateFormOpen] = useState(false);
    const [selectedGateDriver, setSelectedGateDriver] = useState(null);
    const [selectedGateRecord, setSelectedGateRecord] = useState(null);
    const [selectedGateMonth, setSelectedGateMonth] = useState('');
    const [gateSelectionMode, setGateSelectionMode] = useState('random'); // 'random' or 'manual'
    const [gateForm, setGateForm] = useState({
        vehicle: '',
        cpcCardPresent: false,
        cpcExpiryDate: '',
        driversLicencePresent: false,
        defectsReported: '',
        defectsMissed: '',
        gateAuditPassed: false
    });

    // Sticker Test State
    const [stickerTestHistory, setStickerTestHistory] = useState([]);
    const [stickerTestFormOpen, setStickerTestFormOpen] = useState(false);
    const [stickerTestForm, setStickerTestForm] = useState({
        driver: '',
        bus: '',
        location: '',
        date: ''
    });
    const [gateSubTab, setGateSubTab] = useState('gateAudit'); // 'gateAudit' or 'stickerTest'
    const [selectedStickerTestMonth, setSelectedStickerTestMonth] = useState('');

    // Ops Disc Management State
    const [vehicles, setVehicles] = useState([]);
    const [showNewVehicleForm, setShowNewVehicleForm] = useState(false);
    const [newVehicleInput, setNewVehicleInput] = useState('');
    const [vehicleSearchInput, setVehicleSearchInput] = useState('');
    const [assigningOpsDiscRecord, setAssigningOpsDiscRecord] = useState(null);
    const [assignVehicleSearchInput, setAssignVehicleSearchInput] = useState('');
    const [assignVehicleReg, setAssignVehicleReg] = useState('');
    const [auditSheetOpen, setAuditSheetOpen] = useState(false);
    const [opsDiscHistory, setOpsDiscHistory] = useState([]);
    const [opsDiscFormOpen, setOpsDiscFormOpen] = useState(false);
    const [selectedOpsDiscRecord, setSelectedOpsDiscRecord] = useState(null);
    const [opsDiscForm, setOpsDiscForm] = useState({
        licenceNumber: '205',
        vehicleReg: '',
        status: 'unassigned'
    });

    // Cleanliness Check State
    const [cleanlinessHistory, setCleanlinessHistory] = useState([]);
    const [cleanlinessFormOpen, setCleanlinessFormOpen] = useState(false);
    const [selectedCleanlinessDriver, setSelectedCleanlinessDriver] = useState(null);
    const [selectedCleanlinessRecord, setSelectedCleanlinessRecord] = useState(null);
    const [cleanlinessSelectionMode, setCleanlinessSelectionMode] = useState('random'); // 'random' or 'manual'
    const [cleanlinessForm, setCleanlinessForm] = useState({
        busReg: '',
        driverArea: '',
        floor: '',
        seats: '',
        windows: '',
        racks: '',
        locker: '',
        toilet: '',
        exterior: ''
    });

    // Infringement Monitor State
    const [infringementHistory, setInfringementHistory] = useState([]);
    const [selectedInfringementMonth, setSelectedInfringementMonth] = useState('');
    const [infringementFormOpen, setInfringementFormOpen] = useState(false);
    const [infringementForm, setInfringementForm] = useState({
        driver: '',
        date: '',
        type: 'WTD',
        debriefDate: ''
    });

    // Missing Walk Round State
    const [missingWalkRoundHistory, setMissingWalkRoundHistory] = useState([]);
    const [selectedMissingWalkRoundMonth, setSelectedMissingWalkRoundMonth] = useState('');
    const [missingWalkRoundFormOpen, setMissingWalkRoundFormOpen] = useState(false);
    const [missingWalkRoundForm, setMissingWalkRoundForm] = useState({
        driver: '',
        date: '',
        reason: ''
    });

    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const complianceType = dashboardTab === 'drugAlcohol' ? drugAlcoholTab : dashboardTab;
    const activeLabel = dashboardTab === 'drugAlcohol' ? 'Drug & Alcohol' : dashboardTab === 'cleanliness' ? 'Cleanliness Checks' : 'Gate Audits';

    const drivers = [...usersList]
      .filter(u => u.drivingStaff || u.jobRole === 'Full Time Driver' || u.jobRole === 'Part Time Driver')
      .sort((a, b) => String(a.displayName || a.initials || '').localeCompare(String(b.displayName || b.initials || ''), undefined, { sensitivity: 'base' }));

    const fetchComplianceRecords = async () => {
        if (complianceType === 'gate' && selectedGateMonth === '') {
            setComplianceRecords([]);
            return;
        }

        const q = query(
            collection(db, 'compliance_tests'),
            where('year', '==', currentDate.getFullYear()),
            where('month', '==', complianceType === 'gate' ? Number(selectedGateMonth) : currentDate.getMonth()),
            where('type', '==', complianceType)
        );
        const s = await getDocs(q);
        setComplianceRecords(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchGateHistory = async () => {
        const q = query(collection(db, 'compliance_tests'), where('type', '==', 'gate'));
        const s = await getDocs(q);
        setGateHistory(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchStickerTestHistory = async () => {
        let s;
        if (selectedStickerTestMonth === '') {
            const q = query(collection(db, 'sticker_tests'));
            s = await getDocs(q);
        } else {
            const q = query(
                collection(db, 'sticker_tests'),
                where('year', '==', currentDate.getFullYear()),
                where('month', '==', Number(selectedStickerTestMonth))
            );
            s = await getDocs(q);
        }
        setStickerTestHistory(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchCleanlinessHistory = async () => {
        const q = query(collection(db, 'compliance_tests'), where('type', '==', 'cleanliness'));
        const s = await getDocs(q);
        setCleanlinessHistory(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchOpsDiscHistory = async () => {
        const q = query(collection(db, 'ops_disc_management'));
        const s = await getDocs(q);
        setOpsDiscHistory(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchVehicles = async () => {
        const q = query(collection(db, 'vehicles'));
        const s = await getDocs(q);
        setVehicles(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchInfringementHistory = async () => {
        const q = query(collection(db, 'infringements'));
        const s = await getDocs(q);
        setInfringementHistory(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const fetchMissingWalkRoundHistory = async () => {
        const q = query(collection(db, 'missing_walk_rounds'));
        const s = await getDocs(q);
        setMissingWalkRoundHistory(s.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const seedMissingWalkRoundData = async () => {
        // Check if data already exists
        const q = query(collection(db, 'missing_walk_rounds'));
        const s = await getDocs(q);
        if (s.docs.length > 0) return; // Data already seeded

        const reasons = ['phone not working', 'showing on phone', 'forgot to input on app'];
        
        // Create sample records for multiple months with no driver repeats
        let driverIndex = 0;
        const sampleData = [];

        // February: 8 records
        sampleData.push(
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-05', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-08', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-12', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-15', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-18', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-22', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-25', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-02-28', reason: 'showing on phone' }
        );

        // March: 7 records
        sampleData.push(
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-03-03', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-03-07', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-03-11', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-03-14', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-03-17', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-03-21', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-03-25', reason: 'forgot to input on app' }
        );

        // April: 9 records
        sampleData.push(
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-02', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-06', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-09', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-13', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-16', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-20', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-23', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-27', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-04-30', reason: 'forgot to input on app' }
        );

        // May: 6 records
        sampleData.push(
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-05-04', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-05-08', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-05-12', reason: 'phone not working' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-05-16', reason: 'showing on phone' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-05-20', reason: 'forgot to input on app' },
            { driver: drivers[driverIndex++]?.displayName || 'Driver', date: '2026-05-24', reason: 'phone not working' }
        );

        for (const data of sampleData) {
            const recordDate = new Date(data.date);
            await addDoc(collection(db, 'missing_walk_rounds'), {
                driver: data.driver,
                date: data.date,
                reason: data.reason,
                year: recordDate.getFullYear(),
                month: recordDate.getMonth(),
                day: recordDate.getDate(),
                timestamp: new Date()
            });
        }
        await fetchMissingWalkRoundHistory();
    };

    useEffect(() => {
        fetchComplianceRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [complianceType, currentDate, selectedGateMonth]);

    useEffect(() => {
        fetchGateHistory();
        fetchStickerTestHistory();
        fetchOpsDiscHistory();
        fetchVehicles();
        fetchInfringementHistory();
        fetchMissingWalkRoundHistory();
        seedMissingWalkRoundData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStickerTestMonth, currentDate]);

    const getLatestGateRecord = (initials) => {
        const records = gateHistory
            .filter(r => r.initials === initials)
            .sort((a, b) => {
                const aDate = a.timestamp?.toDate ? a.timestamp.toDate() : a.timestamp;
                const bDate = b.timestamp?.toDate ? b.timestamp.toDate() : b.timestamp;
                return bDate - aDate;
            });
        return records[0];
    };

    const getLatestCleanlinessRecord = (initials) => {
        const records = cleanlinessHistory
            .filter(r => r.initials === initials)
            .sort((a, b) => {
                const aDate = a.timestamp?.toDate ? a.timestamp.toDate() : a.timestamp;
                const bDate = b.timestamp?.toDate ? b.timestamp.toDate() : b.timestamp;
                return bDate - aDate;
            });
        return records[0];
    };

    const hasPassedRecently = (initials) => {
        const latest = getLatestGateRecord(initials);
        if (!latest || latest.result !== 'Pass') return false;

        const latestDate = latest.timestamp?.toDate ? latest.timestamp.toDate() : latest.timestamp;
        const ageDays = (new Date() - latestDate) / (1000 * 60 * 60 * 24);

        return ageDays < 90; // Less than 90 days ago
    };

    const eligibleGateDrivers = drivers.filter(driver => {
        const latest = getLatestGateRecord(driver.initials);
        if (!latest) return true;

        const latestDate = latest.timestamp?.toDate ? latest.timestamp.toDate() : latest.timestamp;
        const ageDays = (new Date() - latestDate) / (1000 * 60 * 60 * 24);

        if (latest.result === 'Fail') return true;
        return ageDays >= 90;
    });

    const eligibleCleanlinessDrivers = drivers.filter(driver => {
        const latest = getLatestCleanlinessRecord(driver.initials);
        if (!latest) return true;

        const latestDate = latest.timestamp?.toDate ? latest.timestamp.toDate() : latest.timestamp;
        const ageDays = (new Date() - latestDate) / (1000 * 60 * 60 * 24);

        if (latest.result === 'Fail') return true;
        return ageDays >= 60; // 2 months for cleanliness pass
    });

    const handleStartGateAudit = () => {
        if (!eligibleGateDrivers.length) return;
        const randomIndex = Math.floor(Math.random() * eligibleGateDrivers.length);
        const selected = eligibleGateDrivers[randomIndex];
        setSelectedGateDriver(selected);
        setSelectedGateRecord(null);
        setGateForm({
            vehicle: '',
            cpcCardPresent: false,
            cpcExpiryDate: '',
            driversLicencePresent: false,
            defectsReported: '',
            defectsMissed: '',
            gateAuditPassed: false
        });
        setGateFormOpen(true);
    };

    const handleRerollGateDriver = () => {
        if (!selectedGateDriver) return;
        const alternatives = eligibleGateDrivers.filter(d => d.initials !== selectedGateDriver.initials);
        if (!alternatives.length) return;
        const randomIndex = Math.floor(Math.random() * alternatives.length);
        setSelectedGateDriver(alternatives[randomIndex]);
        setSelectedGateRecord(null);
        setGateForm({
            vehicle: '',
            cpcCardPresent: false,
            cpcExpiryDate: '',
            driversLicencePresent: false,
            defectsReported: '',
            defectsMissed: '',
            gateAuditPassed: false
        });
    };

    const handleManualGateDriverSelect = (driver) => {
        setSelectedGateDriver(driver);
        setSelectedGateRecord(null);
        setGateForm({
            vehicle: '',
            cpcCardPresent: false,
            cpcExpiryDate: '',
            driversLicencePresent: false,
            defectsReported: '',
            defectsMissed: '',
            gateAuditPassed: false
        });
        setGateFormOpen(true);
    };

    const handleGateFormChange = (field, value) => {
        setGateForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveGateAudit = async () => {
        if (!selectedGateDriver) return;

        const record = {
            initials: selectedGateDriver.initials,
            displayName: selectedGateDriver.displayName,
            type: 'gate',
            result: gateForm.gateAuditPassed ? 'Pass' : 'Fail',
            year: currentDate.getFullYear(),
            month: currentDate.getMonth(),
            day: currentDate.getDate(),
            timestamp: new Date(),
            vehicle: gateForm.vehicle,
            cpcCardPresent: gateForm.cpcCardPresent,
            cpcExpiryDate: gateForm.cpcExpiryDate,
            driversLicencePresent: gateForm.driversLicencePresent,
            defectsReported: gateForm.defectsReported,
            defectsMissed: gateForm.defectsMissed,
            gateAuditPassed: gateForm.gateAuditPassed
        };

        await addDoc(collection(db, 'compliance_tests'), record);
        await fetchComplianceRecords();
        await fetchGateHistory();
        setGateFormOpen(false);
        setSelectedGateDriver(null);
    };

    const handleNewStickerTest = () => {
        setStickerTestForm({
            driver: '',
            bus: '',
            location: '',
            date: new Date().toISOString().split('T')[0] // Set default to today's date
        });
        setStickerTestFormOpen(true);
    };

    const handleStickerTestFormChange = (field, value) => {
        setStickerTestForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveStickerTest = async () => {
        if (!stickerTestForm.driver || !stickerTestForm.bus || !stickerTestForm.location || !stickerTestForm.date) return;

        const dt = new Date(stickerTestForm.date);
        const record = {
            driver: stickerTestForm.driver,
            bus: stickerTestForm.bus,
            location: stickerTestForm.location,
            date: stickerTestForm.date,
            year: dt.getFullYear(),
            month: dt.getMonth(),
            day: dt.getDate(),
            result: null, // Will be set when Found/Missed is clicked
            timestamp: dt
        };

        await addDoc(collection(db, 'sticker_tests'), record);
        await fetchStickerTestHistory();
        setStickerTestFormOpen(false);
    };

    const handleStickerTestResult = async (id, result) => {
        const testRef = doc(db, 'sticker_tests', id);
        await updateDoc(testRef, { result });
        await fetchStickerTestHistory();
    };

    const handleDeleteStickerTest = async (id) => {
        if (window.confirm('Delete sticker test?')) {
            await deleteDoc(doc(db, 'sticker_tests', id));
            await fetchStickerTestHistory();
        }
    };

    const handleStartCleanlinessCheck = () => {
        if (!eligibleCleanlinessDrivers.length) return;
        const randomIndex = Math.floor(Math.random() * eligibleCleanlinessDrivers.length);
        const selected = eligibleCleanlinessDrivers[randomIndex];
        setSelectedCleanlinessDriver(selected);
        setSelectedCleanlinessRecord(null);
        setCleanlinessForm({
            busReg: '',
            driverArea: '',
            floor: '',
            seats: '',
            windows: '',
            racks: '',
            locker: '',
            toilet: '',
            exterior: ''
        });
        setCleanlinessFormOpen(true);
    };

    const handleManualCleanlinessDriverSelect = (driver) => {
        setSelectedCleanlinessDriver(driver);
        setSelectedCleanlinessRecord(null);
        setCleanlinessForm({
            busReg: '',
            driverArea: '',
            floor: '',
            seats: '',
            windows: '',
            racks: '',
            locker: '',
            toilet: '',
            exterior: ''
        });
        setCleanlinessFormOpen(true);
    };

    const handleRerollCleanlinessDriver = () => {
        if (!selectedCleanlinessDriver) return;
        const alternatives = eligibleCleanlinessDrivers.filter(d => d.initials !== selectedCleanlinessDriver.initials);
        if (!alternatives.length) return;
        const randomIndex = Math.floor(Math.random() * alternatives.length);
        setSelectedCleanlinessDriver(alternatives[randomIndex]);
        setSelectedCleanlinessRecord(null);
        setCleanlinessForm({
            busReg: '',
            driverArea: '',
            floor: '',
            seats: '',
            windows: '',
            racks: '',
            locker: '',
            toilet: '',
            exterior: ''
        });
    };

    const handleCleanlinessFormChange = (field, value) => {
        setCleanlinessForm(prev => ({ ...prev, [field]: value }));
    };

    const calculateCleanlinessScore = () => {
        const scores = {
            'Excellent': 3,
            'Good': 2,
            'Acceptable': 1,
            'Poor': 0,
            'Not Acceptable': -15,
            'N/A': 1
        };
        const criteria = ['driverArea', 'floor', 'seats', 'windows', 'racks', 'locker', 'toilet', 'exterior'];
        let total = 0;
        criteria.forEach(criterion => {
            const value = cleanlinessForm[criterion];
            if (value && scores[value] !== undefined) {
                total += scores[value];
            }
        });
        return total;
    };

    const handleSaveCleanlinessCheck = async () => {
        if (!selectedCleanlinessDriver) return;

        const score = calculateCleanlinessScore();
        const passed = score > 8;

        const record = {
            initials: selectedCleanlinessDriver.initials,
            displayName: selectedCleanlinessDriver.displayName,
            type: 'cleanliness',
            result: passed ? 'Pass' : 'Fail',
            score: score,
            year: currentDate.getFullYear(),
            month: currentDate.getMonth(),
            day: currentDate.getDate(),
            timestamp: new Date(),
            busReg: cleanlinessForm.busReg,
            driverArea: cleanlinessForm.driverArea,
            floor: cleanlinessForm.floor,
            seats: cleanlinessForm.seats,
            windows: cleanlinessForm.windows,
            racks: cleanlinessForm.racks,
            locker: cleanlinessForm.locker,
            toilet: cleanlinessForm.toilet,
            exterior: cleanlinessForm.exterior
        };

        await addDoc(collection(db, 'compliance_tests'), record);
        await fetchComplianceRecords();
        await fetchCleanlinessHistory();
        setCleanlinessFormOpen(false);
        setSelectedCleanlinessDriver(null);
    };

    const handleDeleteTest = async (id) => {
        if (window.confirm('Delete record?')) {
            await deleteDoc(doc(db, 'compliance_tests', id));
            await fetchComplianceRecords();
            await fetchGateHistory();
            await fetchCleanlinessHistory();
            if (selectedGateRecord?.id === id) setSelectedGateRecord(null);
            if (selectedCleanlinessRecord?.id === id) setSelectedCleanlinessRecord(null);
        }
    };

    const handleRecordTest = async (user, result) => {
        await addDoc(collection(db, 'compliance_tests'), {
            initials: user.initials,
            displayName: user.displayName,
            type: complianceType,
            result,
            year: currentDate.getFullYear(),
            month: currentDate.getMonth(),
            day: currentDate.getDate(),
            timestamp: new Date()
        });
        await fetchComplianceRecords();
    };

    const handleInfringementFormChange = (field, value) => {
        setInfringementForm(prev => ({ ...prev, [field]: value }));
    };

    const handleNewInfringement = () => {
        setInfringementForm({
            driver: '',
            date: '',
            type: 'WTD',
            debriefDate: ''
        });
        setInfringementFormOpen(true);
    };

    const handleSaveInfringement = async () => {
        if (!infringementForm.driver || !infringementForm.date || !infringementForm.type) return;
        
        const recordDate = new Date(infringementForm.date);
        await addDoc(collection(db, 'infringements'), {
            driver: infringementForm.driver,
            date: infringementForm.date,
            type: infringementForm.type,
            debriefDate: infringementForm.debriefDate,
            year: recordDate.getFullYear(),
            month: recordDate.getMonth(),
            day: recordDate.getDate(),
            timestamp: new Date()
        });
        await fetchInfringementHistory();
        setInfringementFormOpen(false);
        setInfringementForm({
            driver: '',
            date: '',
            type: 'WTD',
            debriefDate: ''
        });
    };

    const handleDeleteInfringement = async (id) => {
        if (window.confirm('Delete infringement record?')) {
            await deleteDoc(doc(db, 'infringements', id));
            await fetchInfringementHistory();
        }
    };

    const handleMissingWalkRoundFormChange = (field, value) => {
        setMissingWalkRoundForm(prev => ({ ...prev, [field]: value }));
    };

    const handleNewMissingWalkRound = () => {
        setMissingWalkRoundForm({
            driver: '',
            date: '',
            reason: ''
        });
        setMissingWalkRoundFormOpen(true);
    };

    const handleSaveMissingWalkRound = async () => {
        if (!missingWalkRoundForm.driver || !missingWalkRoundForm.date || !missingWalkRoundForm.reason) return;
        
        const recordDate = new Date(missingWalkRoundForm.date);
        await addDoc(collection(db, 'missing_walk_rounds'), {
            driver: missingWalkRoundForm.driver,
            date: missingWalkRoundForm.date,
            reason: missingWalkRoundForm.reason,
            year: recordDate.getFullYear(),
            month: recordDate.getMonth(),
            day: recordDate.getDate(),
            timestamp: new Date()
        });
        await fetchMissingWalkRoundHistory();
        setMissingWalkRoundFormOpen(false);
        setMissingWalkRoundForm({
            driver: '',
            date: '',
            reason: ''
        });
    };

    const handleDeleteMissingWalkRound = async (id) => {
        if (window.confirm('Delete missing walk round record?')) {
            await deleteDoc(doc(db, 'missing_walk_rounds', id));
            await fetchMissingWalkRoundHistory();
        }
    };

    const handleOpsDiscFormChange = (field, value) => {
        setOpsDiscForm(prev => ({ ...prev, [field]: value }));
    };

    const handleAddVehicle = async () => {
        if (!newVehicleInput.trim()) return;
        await addDoc(collection(db, 'vehicles'), { registration: newVehicleInput.trim() });
        setNewVehicleInput('');
        setShowNewVehicleForm(false);
        await fetchVehicles();
    };

    const seedDefaultVehicles = async () => {
        const vehicleList = [
            'S1 CLN', 'T1 CLN', 'V99 CLN', 'A18 CLN', 'A9 CLN', 'CN06 HOL', 'CN05 HOL', 'L10 NKK', 'L70 CLN', 'W70 CLN',
            'M70 CLN', 'J70 CLN', 'L1 ONB', 'L1 ONC', 'L1 OND', 'BU11 JSF', 'JB 3890', 'E1 CLN', 'W1 CLN', 'F1 CLN',
            'G1 CLN', 'A10 CLC', 'A8 CLN', 'A19 CLN', 'X66 CLN', 'T66 CLN', 'V66 CLN', 'A15 CLN', 'CN07 HOL', 'L10 NCC',
            'L10 NHH', 'L10 NBB', 'P4 CLN', 'P5 CLN', '895 CLN', 'L1 CLN', 'T99 JSF', 'L1 ONU', 'TAA 774', 'L7 JSF',
            'PVV 888J', 'V16 CLN', 'W16 CLN', 'Y16 CLN', 'E7 CLN', 'PRP 3V', 'A15 NFC', 'X31 MCJ', 'M11 JMJ', 'YNH 19W',
            'TSO 30X', 'C3 CLN', 'B1 CLN', 'A20 CLC', 'A17 CLN', 'A12 CLN', 'A16 CLC', 'A15 CLC', 'A14 CLC', 'K70 CLN'
        ];

        for (const registration of vehicleList) {
            const existingQuery = query(collection(db, 'vehicles'), where('registration', '==', registration));
            const existing = await getDocs(existingQuery);
            if (existing.empty) {
                await addDoc(collection(db, 'vehicles'), { registration });
            }
        }
        await fetchVehicles();
    };

    const seedOpsDiscAssignments = async () => {
        const assignments = [
            { licenceNumber: '205061', registration: '' },
            { licenceNumber: '205062', registration: 'E7 CLN' },
            { licenceNumber: '205063', registration: 'TSO 30X' },
            { licenceNumber: '205064', registration: 'YNH 19W' },
            { licenceNumber: '205067', registration: 'X31 MCJ' },
            { licenceNumber: '205068', registration: 'PRP 3V' },
            { licenceNumber: '205069', registration: 'C3 CLN' },
            { licenceNumber: '205070', registration: 'B1 CLN' },
            { licenceNumber: '205071', registration: '' },
            { licenceNumber: '205072', registration: 'V16 CLN' },
            { licenceNumber: '205073', registration: 'W16 CLN' },
            { licenceNumber: '205074', registration: 'W70 CLN' },
            { licenceNumber: '205075', registration: 'A17 CLN' },
            { licenceNumber: '205076', registration: 'A12 CLN' },
            { licenceNumber: '205077', registration: 'A16 CLC' },
            { licenceNumber: '205078', registration: 'A14 CLC' },
            { licenceNumber: '205079', registration: 'A15 CLC' },
            { licenceNumber: '205080', registration: 'A20 CLC' },
            { licenceNumber: '205081', registration: 'K70 CLN' },
            { licenceNumber: '205082', registration: 'L70 CLN' },
            { licenceNumber: '205083', registration: 'M70 CLN' },
            { licenceNumber: '205084', registration: 'J70 CLN' },
            { licenceNumber: '205085', registration: 'L1 CLN' },
            { licenceNumber: '205086', registration: 'T99 JSF' },
            { licenceNumber: '205088', registration: 'TAA 774' },
            { licenceNumber: '205089', registration: 'L1 ONB' },
            { licenceNumber: '205090', registration: 'L1ONC' },
            { licenceNumber: '205092', registration: 'L1 ONU' },
            { licenceNumber: '205094', registration: 'BU11 JSF' },
            { licenceNumber: '205095', registration: 'E1 CLN' },
            { licenceNumber: '205096', registration: '' },
            { licenceNumber: '205097', registration: 'F1 CLN' },
            { licenceNumber: '205098', registration: 'G1 CLN' },
            { licenceNumber: '205100', registration: 'A8 CLN' },
            { licenceNumber: '205101', registration: 'A19 CLN' },
            { licenceNumber: '205102', registration: 'V99 CLN' },
            { licenceNumber: '205103', registration: 'V66 CLN' },
            { licenceNumber: '205104', registration: 'X66 CLN' },
            { licenceNumber: '205106', registration: 'A15 CLN' },
            { licenceNumber: '205107', registration: 'S1 CLN' },
            { licenceNumber: '205108', registration: 'T1 CLN' },
            { licenceNumber: '205109', registration: 'A18 CLN' },
            { licenceNumber: '205110', registration: 'A9 CLN' },
            { licenceNumber: '205111', registration: 'CN05 HOL' },
            { licenceNumber: '205112', registration: 'CN06 HOL' },
            { licenceNumber: '205113', registration: 'L10 NKK' },
            { licenceNumber: '205114', registration: 'L10NCC' },
            { licenceNumber: '205115', registration: 'L10NHH' },
            { licenceNumber: '205116', registration: 'L10NBB' },
            { licenceNumber: '205117', registration: 'CN07 HOL' },
            { licenceNumber: '205118', registration: 'P4 CLN' },
            { licenceNumber: '205119', registration: 'P5 CLN' },
            { licenceNumber: '205120', registration: '888J' },
            { licenceNumber: '205121', registration: 'W1 CLN' },
            { licenceNumber: '205124', registration: 'Y16 CLN' },
            { licenceNumber: '205125', registration: 'A15 NFC' },
            { licenceNumber: '205126', registration: '895 CLN' },
            { licenceNumber: '205127', registration: '' },
            { licenceNumber: '205128', registration: '' },
            { licenceNumber: '205129', registration: '' },
            { licenceNumber: '205130', registration: 'M11 JMJ' },
            { licenceNumber: '217593', registration: '' },
            { licenceNumber: '220282', registration: '' }
        ];

        for (const { registration, licenceNumber } of assignments) {
            if (registration) {
                const vehicleQuery = query(collection(db, 'vehicles'), where('registration', '==', registration));
                const vehicleSnap = await getDocs(vehicleQuery);
                if (vehicleSnap.empty) {
                    await addDoc(collection(db, 'vehicles'), { registration });
                }
            }

            const recordQuery = query(collection(db, 'ops_disc_management'), where('licenceNumber', '==', licenceNumber));
            const recordSnap = await getDocs(recordQuery);
            if (recordSnap.empty) {
                await addDoc(collection(db, 'ops_disc_management'), {
                    licenceNumber,
                    vehicleReg: registration || '',
                    status: registration ? 'assigned' : 'unassigned',
                    year: currentDate.getFullYear(),
                    month: currentDate.getMonth(),
                    day: currentDate.getDate(),
                    timestamp: new Date()
                });
            }
        }

        await fetchVehicles();
        await fetchOpsDiscHistory();
    };

    const handleNewOpsDisc = () => {
        setOpsDiscForm({
            licenceNumber: '205',
            vehicleReg: '',
            status: 'unassigned'
        });
        setVehicleSearchInput('');
        setOpsDiscFormOpen(true);
    };

    const handleSaveOpsDisc = async () => {
        if (!opsDiscForm.licenceNumber) return;

        const record = {
            licenceNumber: opsDiscForm.licenceNumber,
            vehicleReg: opsDiscForm.vehicleReg || '',
            status: opsDiscForm.vehicleReg ? 'assigned' : opsDiscForm.status === 'in_draw' ? 'in_draw' : 'unassigned',
            year: currentDate.getFullYear(),
            month: currentDate.getMonth(),
            day: currentDate.getDate(),
            timestamp: new Date()
        };

        if (opsDiscForm.status === 'assigned' && !opsDiscForm.vehicleReg) {
            record.status = 'unassigned';
        }

        await addDoc(collection(db, 'ops_disc_management'), record);
        await fetchOpsDiscHistory();
        setOpsDiscFormOpen(false);
        setOpsDiscForm({
            licenceNumber: '205',
            vehicleReg: '',
            status: 'unassigned'
        });
    };

    const handleDeleteOpsDisc = async (id) => {
        if (window.confirm('Delete ops disc record?')) {
            await deleteDoc(doc(db, 'ops_disc_management', id));
            await fetchOpsDiscHistory();
            if (selectedOpsDiscRecord?.id === id) setSelectedOpsDiscRecord(null);
        }
    };

    const handleUpdateOpsDiscStatus = async (id, update) => {
        const recordRef = doc(db, 'ops_disc_management', id);
        await updateDoc(recordRef, update);
        await fetchOpsDiscHistory();
    };

    const handleStartAssignVehicle = (record) => {
        setAssigningOpsDiscRecord(record);
        setAssignVehicleReg(record.vehicleReg || '');
        setAssignVehicleSearchInput('');
    };

    const handleSaveAssignedVehicle = async () => {
        if (!assigningOpsDiscRecord || !assignVehicleReg.trim()) return;
        const recordRef = doc(db, 'ops_disc_management', assigningOpsDiscRecord.id);
        await updateDoc(recordRef, {
            vehicleReg: assignVehicleReg.trim(),
            status: 'assigned'
        });
        await fetchOpsDiscHistory();
        setAssigningOpsDiscRecord(null);
        setAssignVehicleReg('');
        setAssignVehicleSearchInput('');
    };

    const handleDeleteVehicle = async (id) => {
        if (window.confirm('Delete this unassigned vehicle?')) {
            await deleteDoc(doc(db, 'vehicles', id));
            await fetchVehicles();
        }
    };

    const handleToggleAuditSheet = () => {
        setAuditSheetOpen(prev => !prev);
    };

    const filteredVehicles = vehicles.filter(v => v.registration.toLowerCase().includes(vehicleSearchInput.toLowerCase()));
    const assignFilteredVehicles = vehicles.filter(v => v.registration.toLowerCase().includes(assignVehicleSearchInput.toLowerCase()));
    const compareByLicence = (a, b) => Number(a.licenceNumber) - Number(b.licenceNumber);
    const auditSheetRows = [...opsDiscHistory].sort(compareByLicence).map(record => ({
        licenceNumber: record.licenceNumber,
        location: record.status === 'assigned' && record.vehicleReg ? `Vehicle ${record.vehicleReg}` : record.status === 'in_draw' ? 'In Draw' : 'Unassigned'
    }));
    const unassignedRecords = opsDiscHistory.filter(r => r.status === 'unassigned').sort(compareByLicence);
    const assignedRecords = opsDiscHistory.filter(r => r.status === 'assigned' && r.vehicleReg).sort(compareByLicence);
    const inDrawRecords = opsDiscHistory.filter(r => r.status === 'in_draw').sort(compareByLicence);
    const assignedVehicles = new Set(assignedRecords.map(r => r.vehicleReg));
    const unassignedVehicles = vehicles.filter(v => !assignedVehicles.has(v.registration));

    const tested = complianceRecords.map(r => r.initials);
    const untested = usersList
        .filter(u => !tested.includes(u.initials))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    const sortedComplianceRecords = [...complianceRecords].sort((a, b) => a.displayName.localeCompare(b.displayName));

    const renderDrugAlcoholContent = () => (
        <>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                <button onClick={() => setDrugAlcoholTab('alcohol')} className={drugAlcoholTab === 'alcohol' ? 'active-btn' : ''} style={{ background: drugAlcoholTab === 'alcohol' ? 'var(--primary)' : 'var(--light)', color: drugAlcoholTab === 'alcohol' ? 'white' : 'var(--text-main)' }}>Alcohol Tests</button>
                <button onClick={() => setDrugAlcoholTab('drugs')} className={drugAlcoholTab === 'drugs' ? 'active-btn' : ''} style={{ background: drugAlcoholTab === 'drugs' ? 'var(--primary)' : 'var(--light)', color: drugAlcoholTab === 'drugs' ? 'white' : 'var(--text-main)' }}>Drug Tests</button>
            </div>

            <Speedometer value={complianceRecords.length} target={drugAlcoholTab === 'alcohol' ? 40 : 2} warning={drugAlcoholTab === 'alcohol' ? 30 : 1} max={drugAlcoholTab === 'alcohol' ? 50 : 5} label="Tests this Month" />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', textAlign: 'left', marginTop: '30px' }}>
                <div className="card">
                    <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>Untested Drivers</h4>
                    {untested.length === 0 ? <p className="text-sm">All drivers have been tested this month.</p> : untested.map(u => (
                        <div key={u.initials} className="flex-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--light)' }}>
                            <span>{u.displayName}</span>
                            <div className="flex-gap">
                                <button onClick={() => handleRecordTest(u, 'Pass')} style={{ padding: '4px 10px', fontSize: '12px', background: 'var(--success)', color: 'white' }}>Pass</button>
                                <button onClick={() => handleRecordTest(u, 'Fail')} style={{ padding: '4px 10px', fontSize: '12px', background: 'var(--danger)', color: 'white' }}>Fail</button>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="card">
                    <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>{activeLabel} Recorded</h4>
                    {sortedComplianceRecords.length === 0 ? <p className="text-sm">No records yet this month.</p> : sortedComplianceRecords.map(r => (
                        <div key={r.id} className="flex-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--light)', background: r.result === 'Fail' ? 'rgba(179,38,30,0.15)' : 'transparent' }}>
                            <span>{r.displayName} <span className={r.result === 'Pass' ? 'text-success' : 'text-danger'}>({r.result})</span></span>
                            <button onClick={() => handleDeleteTest(r.id)} style={{ background: 'transparent', color: 'var(--text-muted)', padding: '4px' }}>✕</button>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );

    const renderCleanlinessContent = () => (
        <div style={{ display: 'grid', gap: '20px', textAlign: 'left', marginTop: '20px' }}>
            <div className="flex-between" style={{ gap: '15px', flexWrap: 'wrap' }}>
                <div className="flex-gap" style={{ gap: '10px', alignItems: 'center' }}>
                    <div className="flex-gap" style={{ gap: '10px' }}>
                        <button 
                            onClick={() => setCleanlinessSelectionMode('random')} 
                            className={cleanlinessSelectionMode === 'random' ? 'active-btn' : ''} 
                            style={{ background: cleanlinessSelectionMode === 'random' ? 'var(--primary)' : 'var(--light)', color: cleanlinessSelectionMode === 'random' ? 'white' : 'var(--text-main)', padding: '8px 16px', borderRadius: '20px', fontSize: '12px' }}
                        >
                            Random
                        </button>
                        <button 
                            onClick={() => setCleanlinessSelectionMode('manual')} 
                            className={cleanlinessSelectionMode === 'manual' ? 'active-btn' : ''} 
                            style={{ background: cleanlinessSelectionMode === 'manual' ? 'var(--primary)' : 'var(--light)', color: cleanlinessSelectionMode === 'manual' ? 'white' : 'var(--text-main)', padding: '8px 16px', borderRadius: '20px', fontSize: '12px' }}
                        >
                            Manual
                        </button>
                    </div>
                    {cleanlinessSelectionMode === 'random' ? (
                        <button onClick={handleStartCleanlinessCheck} disabled={!eligibleCleanlinessDrivers.length} style={{ background: eligibleCleanlinessDrivers.length ? 'var(--primary)' : 'var(--light)', color: eligibleCleanlinessDrivers.length ? 'white' : 'var(--text-muted)', padding: '10px 18px', borderRadius: '50px' }}>
                            Start Cleanliness Check
                        </button>
                    ) : (
                        <select 
                            onChange={(e) => {
                                const selectedDriver = eligibleCleanlinessDrivers.find(d => d.initials === e.target.value);
                                if (selectedDriver) handleManualCleanlinessDriverSelect(selectedDriver);
                            }} 
                            style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', minWidth: '200px' }}
                            defaultValue=""
                        >
                            <option value="" disabled>Select Driver</option>
                            {eligibleCleanlinessDrivers.map(driver => (
                                <option key={driver.initials} value={driver.initials}>{driver.displayName} ({driver.initials})</option>
                            ))}
                        </select>
                    )}
                    {cleanlinessFormOpen && selectedCleanlinessDriver && cleanlinessSelectionMode === 'random' && (
                        <button onClick={handleRerollCleanlinessDriver} disabled={eligibleCleanlinessDrivers.filter(d => d.initials !== selectedCleanlinessDriver.initials).length === 0} style={{ background: 'var(--warning)', color: 'black', padding: '10px 18px', borderRadius: '50px' }}>
                            Reroll Driver
                        </button>
                    )}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    {eligibleCleanlinessDrivers.length ? `${eligibleCleanlinessDrivers.length} drivers eligible for cleanliness check.` : 'No eligible drivers at the moment.'}
                </div>
            </div>

            {cleanlinessFormOpen && selectedCleanlinessDriver && (
                <div className="card" style={{ padding: '20px' }}>
                    <h4 style={{ marginBottom: '15px' }}>New Cleanliness Check</h4>
                    <div style={{ display: 'grid', gap: '15px' }}>
                        <label>
                            <div style={{ marginBottom: '6px', fontWeight: '600' }}>Driver's Name</div>
                            <input type="text" value={selectedCleanlinessDriver.displayName || selectedCleanlinessDriver.initials} readOnly style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                        </label>
                        <label>
                            <div style={{ marginBottom: '6px', fontWeight: '600' }}>Bus Reg</div>
                            <input type="text" value={cleanlinessForm.busReg} onChange={e => handleCleanlinessFormChange('busReg', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                        </label>
                        {[
                            { key: 'driverArea', label: 'Driver Area' },
                            { key: 'floor', label: 'Floor' },
                            { key: 'seats', label: 'Seats' },
                            { key: 'windows', label: 'Windows' },
                            { key: 'racks', label: 'Racks' },
                            { key: 'locker', label: 'Locker' },
                            { key: 'toilet', label: 'Toilet' },
                            { key: 'exterior', label: 'Exterior' }
                        ].map(criterion => (
                            <div key={criterion.key}>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>{criterion.label}</div>
                                <div className="flex-gap" style={{ gap: '10px', flexWrap: 'wrap' }}>
                                    {[
                                        { value: 'Excellent', score: 3 },
                                        { value: 'Good', score: 2 },
                                        { value: 'Acceptable', score: 1 },
                                        { value: 'Poor', score: 0 },
                                        { value: 'Not Acceptable', score: -15 },
                                        { value: 'N/A', score: 1 }
                                    ].map(option => (
                                        <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input 
                                                type="radio" 
                                                name={criterion.key} 
                                                checked={cleanlinessForm[criterion.key] === option.value} 
                                                onChange={() => handleCleanlinessFormChange(criterion.key, option.value)} 
                                            />
                                            {option.value} ({option.score})
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div style={{ marginTop: '15px', padding: '10px', background: 'var(--light)', borderRadius: '8px' }}>
                            <strong>Total Score: {calculateCleanlinessScore()}</strong> (Pass if {'>'} 8)
                        </div>
                        <div className="flex-gap" style={{ marginTop: '10px' }}>
                            <button onClick={() => setCleanlinessFormOpen(false)} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px' }}>Cancel</button>
                            <button onClick={handleSaveCleanlinessCheck} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px' }}>Save Check</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="card" style={{ padding: '20px' }}>
                <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>Saved Cleanliness Checks</h4>
                {complianceRecords.length === 0 ? (
                    <p className="text-sm">No cleanliness checks have been logged this month.</p>
                ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {complianceRecords.map(r => {
                            const timestamp = new Date(r.timestamp?.toDate ? r.timestamp.toDate() : r.timestamp);
                            return (
                                <div key={r.id} style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                                    <button
                                        onClick={() => { setSelectedCleanlinessRecord(r); setCleanlinessFormOpen(false); setSelectedCleanlinessDriver(null); }}
                                        style={{
                                            flex: 1,
                                            textAlign: 'left',
                                            padding: '14px',
                                            borderRadius: '10px',
                                            border: '1px solid var(--border)',
                                            background: selectedCleanlinessRecord?.id === r.id ? 'var(--light)' : 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                                            <strong>{r.displayName || r.initials}</strong>
                                            <span className={r.result === 'Pass' ? 'text-success' : 'text-danger'}>{r.result} ({r.score})</span>
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>{timestamp.toLocaleDateString()} • {r.busReg || 'No bus reg entered'}</div>
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDeleteTest(r.id); }} style={{ background: 'var(--danger)', color: 'white', borderRadius: '10px', border: 'none', padding: '0 14px', minWidth: '40px' }}>✕</button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectedCleanlinessRecord && (
                <div className="card" style={{ padding: '20px' }}>
                    <div className="flex-between" style={{ marginBottom: '15px' }}>
                        <h4 style={{ margin: 0 }}>Cleanliness Check Details</h4>
                        <button onClick={() => setSelectedCleanlinessRecord(null)} style={{ background: 'transparent', border: 'none', fontSize: '18px', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
                    </div>
                    <div style={{ display: 'grid', gap: '10px' }}>
                        <div><strong>Driver:</strong> {selectedCleanlinessRecord.displayName || selectedCleanlinessRecord.initials}</div>
                        <div><strong>Date:</strong> {new Date(selectedCleanlinessRecord.timestamp?.toDate ? selectedCleanlinessRecord.timestamp.toDate() : selectedCleanlinessRecord.timestamp).toLocaleString()}</div>
                        <div><strong>Bus Reg:</strong> {selectedCleanlinessRecord.busReg || '—'}</div>
                        <div><strong>Driver Area:</strong> {selectedCleanlinessRecord.driverArea || '—'}</div>
                        <div><strong>Floor:</strong> {selectedCleanlinessRecord.floor || '—'}</div>
                        <div><strong>Seats:</strong> {selectedCleanlinessRecord.seats || '—'}</div>
                        <div><strong>Windows:</strong> {selectedCleanlinessRecord.windows || '—'}</div>
                        <div><strong>Racks:</strong> {selectedCleanlinessRecord.racks || '—'}</div>
                        <div><strong>Locker:</strong> {selectedCleanlinessRecord.locker || '—'}</div>
                        <div><strong>Toilet:</strong> {selectedCleanlinessRecord.toilet || '—'}</div>
                        <div><strong>Exterior:</strong> {selectedCleanlinessRecord.exterior || '—'}</div>
                        <div><strong>Total Score:</strong> {selectedCleanlinessRecord.score}</div>
                        <div><strong>Result:</strong> {selectedCleanlinessRecord.result}</div>
                        <button onClick={() => handleDeleteTest(selectedCleanlinessRecord.id)} style={{ background: 'var(--danger)', color: 'white', padding: '10px 18px', marginTop: '10px' }}>Delete Record</button>
                    </div>
                </div>
            )}
        </div>
    );

    const renderOpsDiscContent = () => (
        <div style={{ display: 'grid', gap: '20px', textAlign: 'left', marginTop: '20px' }}>
            <div className="card" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                    <div>
                        <h4 style={{ margin: '0 0 10px' }}>Ops Disc Management</h4>
                        <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                            Last audit: 12/12/2025 • Next audit due: 12/06/2026
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button onClick={() => setShowNewVehicleForm(true)} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px', borderRadius: '8px' }}>+ Add Vehicle</button>
                        <button onClick={seedDefaultVehicles} style={{ background: 'var(--warning)', color: 'black', padding: '10px 18px', borderRadius: '8px' }}>Load Vehicle List</button>
                        <button onClick={seedOpsDiscAssignments} style={{ background: 'var(--success)', color: 'white', padding: '10px 18px', borderRadius: '8px' }}>Load Licence Assignments</button>
                        <button onClick={handleToggleAuditSheet} style={{ background: 'var(--secondary)', color: 'white', padding: '10px 18px', borderRadius: '8px' }}>{auditSheetOpen ? 'Close Audit Sheet' : 'Open Audit Sheet'}</button>
                    </div>
                </div>
                {showNewVehicleForm && (
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '15px' }}>
                        <input
                            type="text"
                            value={newVehicleInput}
                            onChange={e => setNewVehicleInput(e.target.value)}
                            placeholder="Enter vehicle registration"
                            style={{ flex: 1, minWidth: '200px', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                        />
                        <button onClick={handleAddVehicle} style={{ background: 'var(--success)', color: 'white', padding: '10px 18px', borderRadius: '8px' }}>Save</button>
                        <button onClick={() => { setShowNewVehicleForm(false); setNewVehicleInput(''); }} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px', borderRadius: '8px' }}>Cancel</button>
                    </div>
                )}
                <div style={{ marginTop: '20px' }}>
                    <h5 style={{ marginBottom: '10px' }}>Unassigned Vehicles ({unassignedVehicles.length})</h5>
                    {unassignedVehicles.length ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
                            {unassignedVehicles.map(v => (
                                <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: 'var(--light)', borderRadius: '8px', fontWeight: '600' }}>
                                    <span>{v.registration}</span>
                                    <button onClick={() => handleDeleteVehicle(v.id)} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm">No unassigned vehicles.</p>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0' }}>
                <button onClick={handleNewOpsDisc} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px', borderRadius: '50px' }}>+ Add Ops Disc</button>
            </div>

            {auditSheetOpen && (
                <div className="card" style={{ padding: '20px' }}>
                    <div className="flex-between" style={{ marginBottom: '15px' }}>
                        <h4 style={{ margin: 0 }}>Audit Sheet</h4>
                        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{auditSheetRows.length} entries</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid var(--border)' }}>Licence Number</th>
                                    <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid var(--border)' }}>Location</th>
                                </tr>
                            </thead>
                            <tbody>
                                {auditSheetRows.map((row, index) => (
                                    <tr key={index}>
                                        <td style={{ padding: '10px', borderBottom: '1px solid var(--border)' }}>{row.licenceNumber}</td>
                                        <td style={{ padding: '10px', borderBottom: '1px solid var(--border)' }}>{row.location}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {opsDiscFormOpen && (
                <div className="card" style={{ padding: '20px' }}>
                    <h4 style={{ marginBottom: '15px' }}>New Ops Disc Record</h4>
                    <div style={{ display: 'grid', gap: '15px' }}>
                        <label>
                            <div style={{ marginBottom: '6px', fontWeight: '600' }}>Licence Number</div>
                            <input type="text" value={opsDiscForm.licenceNumber} onChange={e => handleOpsDiscFormChange('licenceNumber', e.target.value)} placeholder="Enter licence number" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                        </label>
                        <label>
                            <div style={{ marginBottom: '6px', fontWeight: '600' }}>Status</div>
                            <select value={opsDiscForm.status} onChange={e => handleOpsDiscFormChange('status', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                <option value="unassigned">Unassigned</option>
                                <option value="assigned">Assigned</option>
                                <option value="in_draw">In Draw</option>
                            </select>
                        </label>
                        <label>
                            <div style={{ marginBottom: '6px', fontWeight: '600' }}>Vehicle Registration (Optional)</div>
                            <input
                                type="text"
                                value={vehicleSearchInput}
                                onChange={e => setVehicleSearchInput(e.target.value)}
                                placeholder="Search and select vehicle..."
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                            />
                            {vehicleSearchInput && filteredVehicles.length > 0 && (
                                <div style={{ position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: '0', left: '0', right: '0', background:'var(--light)', border: '1px solid var(--border)', borderRadius: '0 0 8px 8px', maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}>
                                        {filteredVehicles.map(v => (
                                            <div key={v.id} onClick={() => { handleOpsDiscFormChange('vehicleReg', v.registration); setVehicleSearchInput(''); }} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid var(--light)', background: opsDiscForm.vehicleReg === v.registration ? 'var(--light)' : 'transparent' }}>
                                                {v.registration}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {opsDiscForm.vehicleReg && (
                                <div style={{ marginTop: '10px', padding: '10px', background: 'var(--light)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span><strong>{opsDiscForm.vehicleReg}</strong></span>
                                    <button onClick={() => { handleOpsDiscFormChange('vehicleReg', ''); setVehicleSearchInput(''); }} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>✕</button>
                                </div>
                            )}
                        </label>
                        <div className="flex-gap" style={{ marginTop: '10px' }}>
                            <button onClick={() => setOpsDiscFormOpen(false)} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px' }}>Cancel</button>
                            <button onClick={handleSaveOpsDisc} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px' }}>Save Record</button>
                        </div>
                    </div>
                </div>
            )}

            {assigningOpsDiscRecord && (
                <div className="card" style={{ padding: '20px' }}>
                    <div className="flex-between" style={{ marginBottom: '15px' }}>
                        <h4 style={{ margin: 0 }}>Assign Licence {assigningOpsDiscRecord.licenceNumber} to Vehicle</h4>
                        <button onClick={() => setAssigningOpsDiscRecord(null)} style={{ background: 'transparent', border: 'none', fontSize: '18px', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
                    </div>
                    <div style={{ display: 'grid', gap: '15px' }}>
                        <label>
                            <div style={{ marginBottom: '6px', fontWeight: '600' }}>Vehicle Registration</div>
                            <input
                                type="text"
                                value={assignVehicleSearchInput || assignVehicleReg}
                                onChange={e => {
                                    setAssignVehicleSearchInput(e.target.value);
                                    setAssignVehicleReg('');
                                }}
                                placeholder="Search and select vehicle..."
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}
                            />
                        </label>
                        {assignVehicleSearchInput && assignFilteredVehicles.length > 0 && (
                            <div style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', top: '0', left: '0', right: '0', background:'var(--light)', border: '1px solid var(--border)', borderRadius: '0 0 8px 8px', maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}>
                                    {assignFilteredVehicles.map(v => (
                                        <div key={v.id} onClick={() => { setAssignVehicleReg(v.registration); setAssignVehicleSearchInput(''); }} style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid var(--light)', background: assignVehicleReg === v.registration ? 'var(--light)' : 'transparent' }}>
                                            {v.registration}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {assignVehicleReg && (
                            <div style={{ marginTop: '10px', padding: '10px', background: 'var(--light)', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span><strong>{assignVehicleReg}</strong></span>
                                <button onClick={() => { setAssignVehicleReg(''); setAssignVehicleSearchInput(''); }} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>✕</button>
                            </div>
                        )}
                        <div className="flex-gap" style={{ marginTop: '10px' }}>
                            <button onClick={() => setAssigningOpsDiscRecord(null)} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px' }}>Cancel</button>
                            <button onClick={handleSaveAssignedVehicle} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px' }}>Assign Vehicle</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="card" style={{ padding: '20px' }}>
                <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px' }}>Unassigned Licences ({unassignedRecords.length})</h4>
                {unassignedRecords.length === 0 ? (
                    <p className="text-sm">No unassigned licences.</p>
                ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {unassignedRecords.map(record => (
                            <div key={record.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--light)' }}>
                                <div style={{ flex: 1 }}>
                                    <div><strong>Licence Number:</strong> {record.licenceNumber}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{record.vehicleReg ? `Vehicle Registration: ${record.vehicleReg}` : 'No vehicle assigned'}</div>
                                </div>
                                <button onClick={() => handleUpdateOpsDiscStatus(record.id, { status: 'in_draw' })} style={{ background: 'var(--warning)', color: 'black', padding: '8px 12px', borderRadius: '8px' }}>Move to Draw</button>
                                <button onClick={() => handleDeleteOpsDisc(record.id)} style={{ background: 'var(--danger)', color: 'white', borderRadius: '8px', border: 'none', padding: '8px 12px' }}>✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="card" style={{ padding: '20px' }}>
                <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px' }}>Assigned Licences ({assignedRecords.length})</h4>
                {assignedRecords.length === 0 ? (
                    <p className="text-sm">No assigned licences.</p>
                ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {assignedRecords.map(record => (
                            <div key={record.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--light)' }}>
                                <div style={{ flex: 1 }}>
                                    <div><strong>Licence Number:</strong> {record.licenceNumber}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{record.vehicleReg ? `Vehicle Registration: ${record.vehicleReg}` : 'No vehicle assigned'}</div>
                                </div>
                                <button onClick={() => handleUpdateOpsDiscStatus(record.id, { status: 'unassigned', vehicleReg: '' })} style={{ background: 'var(--warning)', color: 'black', padding: '8px 12px', borderRadius: '8px' }}>Unassign</button>
                                <button onClick={() => handleDeleteOpsDisc(record.id)} style={{ background: 'var(--danger)', color: 'white', borderRadius: '8px', border: 'none', padding: '8px 12px' }}>✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="card" style={{ padding: '20px' }}>
                <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px' }}>In The Draw ({inDrawRecords.length})</h4>
                {inDrawRecords.length === 0 ? (
                    <p className="text-sm">No licences in the draw.</p>
                ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                        {inDrawRecords.map(record => (
                            <div key={record.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)', background:'rgba(94,106,210,0.12)' }}>
                                <div style={{ flex: 1 }}>
                                    <div><strong>Licence Number:</strong> {record.licenceNumber}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{record.vehicleReg ? `Vehicle Registration: ${record.vehicleReg}` : 'No vehicle assigned'}</div>
                                </div>
                                <button onClick={() => handleStartAssignVehicle(record)} style={{ background: 'var(--primary)', color: 'white', padding: '8px 12px', borderRadius: '8px' }}>Put In Vehicle</button>
                                <button onClick={() => handleUpdateOpsDiscStatus(record.id, { status: 'unassigned' })} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '8px 12px', borderRadius: '8px' }}>Move to Unassigned</button>
                                <button onClick={() => handleDeleteOpsDisc(record.id)} style={{ background: 'var(--danger)', color: 'white', borderRadius: '8px', border: 'none', padding: '8px 12px' }}>✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderInfringementContent = () => {
        const infringementsByMonth = selectedInfringementMonth !== '' 
            ? infringementHistory.filter(r => r.month === Number(selectedInfringementMonth))
            : infringementHistory.filter(r => r.year === currentDate.getFullYear() && r.month === currentDate.getMonth());
        
        const typeCounts = {
            'WTD': 0,
            'Driver Hours': 0,
            'Daily Rest': 0,
            'Weekly Rest': 0,
            'Incorrect Manual Entry': 0,
            'Failed to Change Mode Switch': 0,
            'Left Card in Vehicle': 0
        };
        
        infringementsByMonth.forEach(record => {
            if (record.type && typeCounts.hasOwnProperty(record.type)) {
                typeCounts[record.type]++;
            }
        });
        
        const maxCount = Math.max(...Object.values(typeCounts), 1);
        
        return (
            <div style={{ display: 'grid', gap: '20px', textAlign: 'left', marginTop: '20px' }}>
                <div className="card" style={{ padding: '20px' }}>
                    <div className="flex-between" style={{ gap: '15px', flexWrap: 'wrap', marginBottom: '20px' }}>
                        <h4 style={{ margin: 0 }}>Infringement Monitor</h4>
                        <div className="flex-gap" style={{ gap: '10px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                                Month:
                                <select value={selectedInfringementMonth} onChange={e => setSelectedInfringementMonth(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                    <option value="">Current Month</option>
                                    {monthNames.map((month, index) => (
                                        <option key={month} value={index}>{month}</option>
                                    ))}
                                </select>
                            </label>
                            <button onClick={handleNewInfringement} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px', borderRadius: '50px' }}>+ Add Infringement</button>
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ marginBottom: '15px' }}>Infringement Count by Type</h5>
                        <div style={{ display: 'grid', gap: '15px' }}>
                            {Object.entries(typeCounts).map(([type, count]) => {
                                const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                return (
                                    <div key={type}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                            <strong>{type}</strong>
                                            <span style={{ background: 'var(--light)', padding: '2px 8px', borderRadius: '4px', fontSize: '13px' }}>{count}</span>
                                        </div>
                                        <div style={{ height: '30px', background: 'var(--light)', borderRadius: '8px', overflow: 'hidden' }}>
                                            <div style={{
                                                height: '100%',
                                                width: `${barWidth}%`,
                                                background: type === 'WTD' ? '#ff6b6b' : type === 'Driver Hours' ? '#ffa94d' : type === 'Daily Rest' ? '#74c0fc' : type === 'Weekly Rest' ? '#51cf66' : type === 'Incorrect Manual Entry' ? '#b197fc' : type === 'Failed to Change Mode Switch' ? '#ff922b' : '#a8e6cf',
                                                transition: 'width 0.3s ease'
                                            }}></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {infringementFormOpen && (
                    <div className="card" style={{ padding: '20px' }}>
                        <div className="flex-between" style={{ marginBottom: '15px' }}>
                            <h4 style={{ margin: 0 }}>Add Infringement</h4>
                            <button onClick={() => setInfringementFormOpen(false)} style={{ background: 'transparent', border: 'none', fontSize: '18px', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
                        </div>
                        <div style={{ display: 'grid', gap: '15px' }}>
                            <label>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>Driver</div>
                                <select value={infringementForm.driver} onChange={e => handleInfringementFormChange('driver', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                    <option value="">Select Driver</option>
                                    {drivers.map(driver => (
                                        <option key={driver.initials} value={driver.displayName || driver.initials}>{driver.displayName || driver.initials}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>Date</div>
                                <input type="date" value={infringementForm.date} onChange={e => handleInfringementFormChange('date', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                            </label>
                            <label>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>Infringement Type</div>
                                <select value={infringementForm.type} onChange={e => handleInfringementFormChange('type', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                    <option value="WTD">WTD</option>
                                    <option value="Driver Hours">Driver Hours</option>
                                    <option value="Daily Rest">Daily Rest</option>
                                    <option value="Weekly Rest">Weekly Rest</option>
                                    <option value="Incorrect Manual Entry">Incorrect Manual Entry</option>
                                    <option value="Failed to Change Mode Switch">Failed to Change Mode Switch</option>
                                    <option value="Left Card in Vehicle">Left Card in Vehicle</option>
                                </select>
                            </label>
                            <label>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>Debrief Date</div>
                                <input type="date" value={infringementForm.debriefDate} onChange={e => handleInfringementFormChange('debriefDate', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                            </label>
                            <div className="flex-gap" style={{ marginTop: '10px' }}>
                                <button onClick={() => setInfringementFormOpen(false)} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px' }}>Cancel</button>
                                <button onClick={handleSaveInfringement} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px' }}>Save Infringement</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="card" style={{ padding: '20px' }}>
                    <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px' }}>Infringements ({infringementsByMonth.length})</h4>
                    {infringementsByMonth.length === 0 ? (
                        <p className="text-sm">No infringements recorded for this month.</p>
                    ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                            {infringementsByMonth.sort((a, b) => new Date(b.date) - new Date(a.date)).map(record => {
                                const recordDate = new Date(record.date);
                                const debriefDate = record.debriefDate ? new Date(record.debriefDate) : null;
                                return (
                                    <div key={record.id} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--light)' }}>
                                        <div style={{ flex: 1 }}>
                                            <div><strong>{record.driver}</strong> - <span style={{ background: 'var(--primary)', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{record.type}</span></div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
                                                Date: {recordDate.toLocaleDateString()} {debriefDate ? `• Debrief: ${debriefDate.toLocaleDateString()}` : ''}
                                            </div>
                                        </div>
                                        <button onClick={() => handleDeleteInfringement(record.id)} style={{ background: 'var(--danger)', color: 'white', borderRadius: '8px', border: 'none', padding: '8px 12px' }}>✕</button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderMissingWalkRoundContent = () => {
        const missingWalkRoundsByMonth = selectedMissingWalkRoundMonth !== '' 
            ? missingWalkRoundHistory.filter(r => r.month === Number(selectedMissingWalkRoundMonth))
            : missingWalkRoundHistory.filter(r => r.year === currentDate.getFullYear() && r.month === currentDate.getMonth());
        
        const sortedRecords = [...missingWalkRoundsByMonth].sort((a, b) => {
            const aDate = new Date(a.date);
            const bDate = new Date(b.date);
            return bDate - aDate;
        });
        
        return (
            <div style={{ display: 'grid', gap: '20px', textAlign: 'left', marginTop: '20px' }}>
                <div className="card" style={{ padding: '20px' }}>
                    <div className="flex-between" style={{ gap: '15px', flexWrap: 'wrap', marginBottom: '20px' }}>
                        <h4 style={{ margin: 0 }}>Missing Walk Rounds</h4>
                        <div className="flex-gap" style={{ gap: '10px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                                Month:
                                <select value={selectedMissingWalkRoundMonth} onChange={e => setSelectedMissingWalkRoundMonth(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                    <option value="">Current Month</option>
                                    {monthNames.map((month, index) => (
                                        <option key={month} value={index}>{month}</option>
                                    ))}
                                </select>
                            </label>
                            <button onClick={handleNewMissingWalkRound} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px', borderRadius: '50px' }}>+ Add Walk Round</button>
                        </div>
                    </div>
                </div>

                {missingWalkRoundFormOpen && (
                    <div className="card" style={{ padding: '20px' }}>
                        <div className="flex-between" style={{ marginBottom: '15px' }}>
                            <h4 style={{ margin: 0 }}>Add Missing Walk Round</h4>
                            <button onClick={() => setMissingWalkRoundFormOpen(false)} style={{ background: 'transparent', border: 'none', fontSize: '18px', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
                        </div>
                        <div style={{ display: 'grid', gap: '15px' }}>
                            <label>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>Driver</div>
                                <select value={missingWalkRoundForm.driver} onChange={e => handleMissingWalkRoundFormChange('driver', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                    <option value="">Select Driver</option>
                                    {drivers.map(driver => (
                                        <option key={driver.initials} value={driver.displayName || driver.initials}>{driver.displayName || driver.initials}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>Date</div>
                                <input type="date" value={missingWalkRoundForm.date} onChange={e => handleMissingWalkRoundFormChange('date', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                            </label>
                            <label>
                                <div style={{ marginBottom: '6px', fontWeight: '600' }}>Reason</div>
                                <select value={missingWalkRoundForm.reason} onChange={e => handleMissingWalkRoundFormChange('reason', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                    <option value="">Select Reason</option>
                                    <option value="phone not working">Phone not working</option>
                                    <option value="showing on phone">Showing on phone</option>
                                    <option value="forgot to input on app">Forgot to input on app</option>
                                </select>
                            </label>
                            <div className="flex-gap" style={{ marginTop: '10px' }}>
                                <button onClick={() => setMissingWalkRoundFormOpen(false)} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px' }}>Cancel</button>
                                <button onClick={handleSaveMissingWalkRound} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px' }}>Save Walk Round</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="card" style={{ padding: '20px' }}>
                    <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '15px' }}>Records ({sortedRecords.length})</h4>
                    {sortedRecords.length === 0 ? (
                        <p className="text-sm">No missing walk rounds recorded for this month.</p>
                    ) : (
                        <div style={{ display: 'grid', gap: '10px' }}>
                            {sortedRecords.map(record => (
                                <div key={record.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--light)', borderRadius: '8px' }}>
                                    <div>
                                        <strong>{record.driver}</strong>
                                        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                            {new Date(record.date).toLocaleDateString('en-GB', { day: 'numeric', month: '2-digit', year: 'numeric' })} • {record.reason}
                                        </div>
                                    </div>
                                    <button onClick={() => handleDeleteMissingWalkRound(record.id)} style={{ background: '#ff6b6b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Delete</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderGateContent = () => (
        <div style={{ display: 'grid', gap: '20px', textAlign: 'left', marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '10px' }}>
                <button onClick={() => setGateSubTab('gateAudit')} className={gateSubTab === 'gateAudit' ? 'active-btn' : ''} style={{ background: gateSubTab === 'gateAudit' ? 'var(--primary)' : 'var(--light)', color: gateSubTab === 'gateAudit' ? 'white' : 'var(--text-main)', padding: '8px 16px', borderRadius: '20px', fontSize: '12px' }}>
                    Gate Audits
                </button>
                <button onClick={() => setGateSubTab('stickerTest')} className={gateSubTab === 'stickerTest' ? 'active-btn' : ''} style={{ background: gateSubTab === 'stickerTest' ? 'var(--primary)' : 'var(--light)', color: gateSubTab === 'stickerTest' ? 'white' : 'var(--text-main)', padding: '8px 16px', borderRadius: '20px', fontSize: '12px' }}>
                    Sticker Test
                </button>
            </div>

            {gateSubTab === 'gateAudit' ? (
                <>
                    <div className="flex-between" style={{ gap: '15px', flexWrap: 'wrap' }}>
                        <div className="flex-gap" style={{ gap: '10px', alignItems: 'center' }}>
                            <div className="flex-gap" style={{ gap: '10px' }}>
                                <button 
                                    onClick={() => setGateSelectionMode('random')} 
                                    className={gateSelectionMode === 'random' ? 'active-btn' : ''} 
                                    style={{ background: gateSelectionMode === 'random' ? 'var(--primary)' : 'var(--light)', color: gateSelectionMode === 'random' ? 'white' : 'var(--text-main)', padding: '8px 16px', borderRadius: '20px', fontSize: '12px' }}
                                >
                                    Random
                                </button>
                                <button 
                                    onClick={() => setGateSelectionMode('manual')} 
                                    className={gateSelectionMode === 'manual' ? 'active-btn' : ''} 
                                    style={{ background: gateSelectionMode === 'manual' ? 'var(--primary)' : 'var(--light)', color: gateSelectionMode === 'manual' ? 'white' : 'var(--text-main)', padding: '8px 16px', borderRadius: '20px', fontSize: '12px' }}
                                >
                                    Manual
                                </button>
                            </div>
                            {gateSelectionMode === 'random' ? (
                                <button onClick={handleStartGateAudit} disabled={!eligibleGateDrivers.length} style={{ background: eligibleGateDrivers.length ? 'var(--primary)' : 'var(--light)', color: eligibleGateDrivers.length ? 'white' : 'var(--text-muted)', padding: '10px 18px', borderRadius: '50px' }}>
                                    Start Gate Audit
                                </button>
                            ) : (
                                <select 
                                    onChange={(e) => {
                                        const selectedDriver = drivers.find(d => d.initials === e.target.value);
                                        if (selectedDriver) handleManualGateDriverSelect(selectedDriver);
                                    }} 
                                    style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--border)', minWidth: '200px' }}
                                    defaultValue=""
                                >
                                    <option value="" disabled>Select Driver</option>
                                    {drivers.map(driver => {
                                        const passedRecently = hasPassedRecently(driver.initials);
                                        return (
                                            <option key={driver.initials} value={driver.initials}>
                                                {driver.displayName} ({driver.initials}){passedRecently ? ' ⚠️' : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            )}
                            {gateFormOpen && selectedGateDriver && gateSelectionMode === 'random' && (
                                <button onClick={handleRerollGateDriver} disabled={eligibleGateDrivers.filter(d => d.initials !== selectedGateDriver.initials).length === 0} style={{ background: 'var(--warning)', color: 'black', padding: '10px 18px', borderRadius: '50px' }}>
                                    Reroll Driver
                                </button>
                            )}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                            {eligibleGateDrivers.length ? `${eligibleGateDrivers.length} drivers eligible for gate audit.` : 'No eligible drivers at the moment.'}
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                            Month:
                            <select value={selectedGateMonth} onChange={e => setSelectedGateMonth(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                <option value="">Select month</option>
                                {monthNames.map((month, index) => (
                                    <option key={month} value={index}>{month}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {gateFormOpen && selectedGateDriver && (
                        <div className="card" style={{ padding: '20px' }}>
                            <h4 style={{ marginBottom: '15px' }}>New Gate Audit</h4>
                            {gateSelectionMode === 'manual' && hasPassedRecently(selectedGateDriver.initials) && (
                                <div style={{ background: 'var(--warning)', color: 'black', padding: '10px', borderRadius: '8px', marginBottom: '15px', fontWeight: '600' }}>
                                    ⚠️ Warning: This driver passed a gate audit less than 90 days ago.
                                </div>
                            )}
                            <div style={{ display: 'grid', gap: '15px' }}>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Driver's Name</div>
                                    <input type="text" value={selectedGateDriver.displayName || selectedGateDriver.initials} readOnly style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Vehicle</div>
                                    <input type="text" value={gateForm.vehicle} onChange={e => handleGateFormChange('vehicle', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <div style={{ display: 'grid', gap: '20px' }}>
                                    <div>
                                        <div style={{ marginBottom: '6px', fontWeight: '600' }}>CPC card present</div>
                                        <div className="flex-gap" style={{ gap: '10px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input type="radio" name="cpcCardPresent" checked={gateForm.cpcCardPresent === true} onChange={() => handleGateFormChange('cpcCardPresent', true)} />
                                                Yes
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input type="radio" name="cpcCardPresent" checked={gateForm.cpcCardPresent === false} onChange={() => handleGateFormChange('cpcCardPresent', false)} />
                                                No
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ marginBottom: '6px', fontWeight: '600' }}>Driver's licence present</div>
                                        <div className="flex-gap" style={{ gap: '10px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input type="radio" name="driversLicencePresent" checked={gateForm.driversLicencePresent === true} onChange={() => handleGateFormChange('driversLicencePresent', true)} />
                                                Yes
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <input type="radio" name="driversLicencePresent" checked={gateForm.driversLicencePresent === false} onChange={() => handleGateFormChange('driversLicencePresent', false)} />
                                                No
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>CPC Expiry Date</div>
                                    <input type="date" value={gateForm.cpcExpiryDate} onChange={e => handleGateFormChange('cpcExpiryDate', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Defects reported</div>
                                    <textarea value={gateForm.defectsReported} onChange={e => handleGateFormChange('defectsReported', e.target.value)} style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Defects missed</div>
                                    <textarea value={gateForm.defectsMissed} onChange={e => handleGateFormChange('defectsMissed', e.target.value)} style={{ width: '100%', minHeight: '80px', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <div>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Gate Audit Passed</div>
                                    <div className="flex-gap" style={{ gap: '10px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input type="radio" name="gateAuditPassed" checked={gateForm.gateAuditPassed === true} onChange={() => handleGateFormChange('gateAuditPassed', true)} />
                                            Yes
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input type="radio" name="gateAuditPassed" checked={gateForm.gateAuditPassed === false} onChange={() => handleGateFormChange('gateAuditPassed', false)} />
                                            No
                                        </label>
                                    </div>
                                </div>
                                <div className="flex-gap" style={{ marginTop: '10px' }}>
                                    <button onClick={() => setGateFormOpen(false)} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px' }}>Cancel</button>
                                    <button onClick={handleSaveGateAudit} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px' }}>Save Audit</button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="card" style={{ padding: '20px' }}>
                        <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>Saved Gate Audits</h4>
                        {selectedGateMonth === '' ? (
                            <p className="text-sm">Select a month to view saved gate audits.</p>
                        ) : complianceRecords.length === 0 ? (
                            <p className="text-sm">No gate audits have been logged for that month.</p>
                        ) : (
                            <div style={{ display: 'grid', gap: '10px' }}>
                                {complianceRecords.map(r => {
                                    const timestamp = new Date(r.timestamp?.toDate ? r.timestamp.toDate() : r.timestamp);
                                    return (
                                        <div key={r.id} style={{ display: 'flex', gap: '10px', alignItems: 'stretch' }}>
                                            <button
                                                onClick={() => { setSelectedGateRecord(r); setGateFormOpen(false); setSelectedGateDriver(null); }}
                                                style={{
                                                    flex: 1,
                                                    textAlign: 'left',
                                                    padding: '14px',
                                                    borderRadius: '10px',
                                                    border: '1px solid var(--border)',
                                                    background: selectedGateRecord?.id === r.id ? 'var(--light)' : 'transparent',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                                                    <strong>{r.displayName || r.initials}</strong>
                                                    <span className={r.result === 'Pass' ? 'text-success' : 'text-danger'}>{r.result}</span>
                                                </div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>{timestamp.toLocaleDateString()} • {r.vehicle || 'No vehicle entered'}</div>
                                            </button>
                                            <button onClick={(e) => { e.stopPropagation(); handleDeleteTest(r.id); }} style={{ background: 'var(--danger)', color: 'white', borderRadius: '10px', border: 'none', padding: '0 14px', minWidth: '40px' }}>✕</button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {selectedGateRecord && (
                        <div className="card" style={{ padding: '20px' }}>
                            <div className="flex-between" style={{ marginBottom: '15px' }}>
                                <h4 style={{ margin: 0 }}>Gate Audit Details</h4>
                                <button onClick={() => setSelectedGateRecord(null)} style={{ background: 'transparent', border: 'none', fontSize: '18px', color: 'var(--text-muted)', cursor: 'pointer' }}>✕</button>
                            </div>
                            <div style={{ display: 'grid', gap: '10px' }}>
                                <div><strong>Driver:</strong> {selectedGateRecord.displayName || selectedGateRecord.initials}</div>
                                <div><strong>Date:</strong> {new Date(selectedGateRecord.timestamp?.toDate ? selectedGateRecord.timestamp.toDate() : selectedGateRecord.timestamp).toLocaleString()}</div>
                                <div><strong>Vehicle:</strong> {selectedGateRecord.vehicle || '—'}</div>
                                <div><strong>CPC card present:</strong> {selectedGateRecord.cpcCardPresent ? 'Yes' : 'No'}</div>
                                <div><strong>CPC expiry:</strong> {selectedGateRecord.cpcExpiryDate || '—'}</div>
                                <div><strong>Driver's licence present:</strong> {selectedGateRecord.driversLicencePresent ? 'Yes' : 'No'}</div>
                                <div><strong>Defects reported:</strong> {selectedGateRecord.defectsReported || 'None'}</div>
                                <div><strong>Defects missed:</strong> {selectedGateRecord.defectsMissed || 'None'}</div>
                                <div><strong>Gate audit passed:</strong> {selectedGateRecord.gateAuditPassed ? 'Yes' : 'No'}</div>
                                <button onClick={() => handleDeleteTest(selectedGateRecord.id)} style={{ background: 'var(--danger)', color: 'white', padding: '10px 18px', marginTop: '10px' }}>Delete Record</button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                        <button onClick={handleNewStickerTest} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px', borderRadius: '50px' }}>
                            New Test
                        </button>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                            Month:
                            <select value={selectedStickerTestMonth} onChange={e => setSelectedStickerTestMonth(e.target.value)} style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                                <option value="">Select month</option>
                                {monthNames.map((month, index) => (
                                    <option key={month} value={index}>{month}</option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {stickerTestFormOpen && (
                        <div className="card" style={{ padding: '20px' }}>
                            <h4 style={{ marginBottom: '15px' }}>New Sticker Test</h4>
                            <div style={{ display: 'grid', gap: '15px' }}>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Driver</div>
                                    <input type="text" value={stickerTestForm.driver} onChange={e => handleStickerTestFormChange('driver', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Bus</div>
                                    <input type="text" value={stickerTestForm.bus} onChange={e => handleStickerTestFormChange('bus', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Location</div>
                                    <input type="text" value={stickerTestForm.location} onChange={e => handleStickerTestFormChange('location', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <label>
                                    <div style={{ marginBottom: '6px', fontWeight: '600' }}>Date</div>
                                    <input type="date" value={stickerTestForm.date} onChange={e => handleStickerTestFormChange('date', e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--border)' }} />
                                </label>
                                <div className="flex-gap" style={{ marginTop: '10px' }}>
                                    <button onClick={() => setStickerTestFormOpen(false)} style={{ background: 'var(--light)', color: 'var(--text-main)', padding: '10px 18px' }}>Cancel</button>
                                    <button onClick={handleSaveStickerTest} style={{ background: 'var(--primary)', color: 'white', padding: '10px 18px' }}>Save Test</button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="card" style={{ padding: '20px' }}>
                        <h4 style={{ borderBottom: '1px solid var(--border)', paddingBottom: '10px' }}>Saved Sticker Tests</h4>
                        {(() => {
                            const filteredTests = selectedStickerTestMonth === '' 
                                ? stickerTestHistory 
                                : stickerTestHistory.filter(test => {
                                    const testDate = new Date(test.timestamp?.toDate ? test.timestamp.toDate() : test.timestamp);
                                    return testDate.getMonth() === Number(selectedStickerTestMonth);
                                });
                            
                            return filteredTests.length === 0 ? (
                                <p className="text-sm">{selectedStickerTestMonth === '' ? 'No sticker tests have been logged yet.' : 'No sticker tests have been logged for that month.'}</p>
                            ) : (
                                <div style={{ display: 'grid', gap: '10px' }}>
                                    {filteredTests.map(test => {
                                        const timestamp = new Date(test.timestamp?.toDate ? test.timestamp.toDate() : test.timestamp);
                                        return (
                                            <div key={test.id} style={{ display: 'flex', gap: '10px', alignItems: 'stretch', padding: '14px', borderRadius: '10px', border: '1px solid var(--border)', background:'var(--light)' }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
                                                        <strong>{test.driver}</strong>
                                                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{timestamp.toLocaleDateString()}</span>
                                                    </div>
                                                    <div style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '10px' }}>
                                                        Bus: {test.bus} • Location: {test.location}
                                                    </div>
                                                    <div className="flex-gap" style={{ gap: '10px' }}>
                                                        <button 
                                                            onClick={() => handleStickerTestResult(test.id, 'Found')} 
                                                            disabled={test.result === 'Found'}
                                                            style={{ 
                                                                background: test.result === 'Found' ? 'var(--success)' : 'var(--light)', 
                                                                color: test.result === 'Found' ? 'white' : 'var(--text-main)', 
                                                                padding: '6px 12px', 
                                                                borderRadius: '20px', 
                                                                fontSize: '12px',
                                                                border: 'none',
                                                                cursor: test.result === 'Found' ? 'default' : 'pointer'
                                                            }}
                                                        >
                                                            Found
                                                        </button>
                                                        <button 
                                                            onClick={() => handleStickerTestResult(test.id, 'Missed')} 
                                                            disabled={test.result === 'Missed'}
                                                            style={{ 
                                                                background: test.result === 'Missed' ? 'var(--danger)' : 'var(--light)', 
                                                                color: test.result === 'Missed' ? 'white' : 'var(--text-main)', 
                                                                padding: '6px 12px', 
                                                                borderRadius: '20px', 
                                                                fontSize: '12px',
                                                                border: 'none',
                                                                cursor: test.result === 'Missed' ? 'default' : 'pointer'
                                                            }}
                                                        >
                                                            Missed
                                                        </button>
                                                    </div>
                                                </div>
                                                <button onClick={() => handleDeleteStickerTest(test.id)} style={{ background: 'var(--danger)', color: 'white', borderRadius: '10px', border: 'none', padding: '0 14px', minWidth: '40px', alignSelf: 'flex-start' }}>✕</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className="card" style={{ textAlign: 'center' }}>
            <h3 style={{ marginBottom: '20px' }}>Compliance Dashboard</h3>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <button onClick={() => setDashboardTab('drugAlcohol')} className={dashboardTab === 'drugAlcohol' ? 'active-btn' : ''} style={{ background: dashboardTab === 'drugAlcohol' ? 'var(--primary)' : 'var(--light)', color: dashboardTab === 'drugAlcohol' ? 'white' : 'var(--text-main)' }}>Drug & Alcohol</button>
                <button onClick={() => setDashboardTab('cleanliness')} className={dashboardTab === 'cleanliness' ? 'active-btn' : ''} style={{ background: dashboardTab === 'cleanliness' ? 'var(--primary)' : 'var(--light)', color: dashboardTab === 'cleanliness' ? 'white' : 'var(--text-main)' }}>Cleanliness Checks</button>
                <button onClick={() => setDashboardTab('gate')} className={dashboardTab === 'gate' ? 'active-btn' : ''} style={{ background: dashboardTab === 'gate' ? 'var(--primary)' : 'var(--light)', color: dashboardTab === 'gate' ? 'white' : 'var(--text-main)' }}>Gate Audits</button>
                <button onClick={() => setDashboardTab('opsDisc')} className={dashboardTab === 'opsDisc' ? 'active-btn' : ''} style={{ background: dashboardTab === 'opsDisc' ? 'var(--primary)' : 'var(--light)', color: dashboardTab === 'opsDisc' ? 'white' : 'var(--text-main)' }}>Ops Disc Management</button>
                <button onClick={() => setDashboardTab('infringement')} className={dashboardTab === 'infringement' ? 'active-btn' : ''} style={{ background: dashboardTab === 'infringement' ? 'var(--primary)' : 'var(--light)', color: dashboardTab === 'infringement' ? 'white' : 'var(--text-main)' }}>Infringement Monitor</button>
                <button onClick={() => setDashboardTab('missingWalkRound')} className={dashboardTab === 'missingWalkRound' ? 'active-btn' : ''} style={{ background: dashboardTab === 'missingWalkRound' ? 'var(--primary)' : 'var(--light)', color: dashboardTab === 'missingWalkRound' ? 'white' : 'var(--text-main)' }}>Missing Walk Rounds</button>
            </div>

            {dashboardTab === 'drugAlcohol' && renderDrugAlcoholContent()}
            {dashboardTab === 'cleanliness' && renderCleanlinessContent()}
            {dashboardTab === 'gate' && renderGateContent()}
            {dashboardTab === 'opsDisc' && renderOpsDiscContent()}
            {dashboardTab === 'infringement' && renderInfringementContent()}
            {dashboardTab === 'missingWalkRound' && renderMissingWalkRoundContent()}
        </div>
    );
};

export default ComplianceDashboard;